import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useFeatureDetails, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Card, Container, Heading3, Separator, Skeleton, tokens } from '@treams/treams-ui';

import { AfasTile } from './products/Afas';
import { EmployesTile } from './products/Employes';
import { GoodHabitzTile } from './products/GoodHabitz';
import { GraphqlApiTile } from './products/GraphqlApi';
import { HR2DayTile } from './products/HR2Day';
import { HoorayTile } from './products/Hooray';
import { LoketTile } from './products/Loket';
import { McpRemoteTile, useCanManageMcpRemote } from './products/McpRemote';
import { MicrosoftTile } from './products/Microsoft';
import { NmbrsTile } from './products/Nmbrs';
import { SDWorxTile } from './products/SDWorx';
import { ScimTile } from './products/Scim';
import { SlackTile } from './products/Slack';
import { StyrTile } from './products/Styr';
import { VismaHRMTile } from './products/VismaHRM';
import { YouForceTile } from './products/YouForce';
import { YouServeTile } from './products/YouServe';

const Grid = styled(Box)`
	display: grid;
	grid-gap: 20px;
	grid-template-columns: repeat(auto-fit, 240px);
`;

export default function Marketplace() {
	const { t } = useTranslation();
	const { canManage: canManageMcp } = useCanManageMcpRemote();
	const { isWorkspaceSuperuser } = useViewerRoles();
	const goodHabitzFeature = useFeatureDetails(graphql.FeatureName.Goodhabitz);
	const apiPermissionsFeature = useFeatureDetails(graphql.FeatureName.ApiPermissions);
	const showDirectApi = isWorkspaceSuperuser && !!apiPermissionsFeature.available;

	const { data: marketplaceData } = graphql.useMarketplaceQuery();
	const { data: apiUsersData } = graphql.useApiUsersQuery({ skip: !showDirectApi });
	const { data: slackData } = graphql.useSlackConfigQuery();
	const { data: teamsData } = graphql.useMicrosoftConfigQuery();
	const { data: mcpData } = graphql.useMcpRemoteSettingsQuery({
		skip: !canManageMcp,
	});

	const workspaceConnector = marketplaceData?.viewer.workspace.connector;
	const hasStyrConnector = !!marketplaceData?.viewer.workspace.styrConfig?.organisationID;
	const hasGoodHabitzConnector = !!marketplaceData?.viewer.workspace.goodHabitzConfig?.enabled;

	return (
		<Container pt={`${tokens.gap.page}px`}>
			<Heading3>{t('settings.connectors.marketplace.hris')}</Heading3>

			<Grid pt={3} pb={4}>
				{marketplaceData ? (
					<>
						<AfasTile selected={workspaceConnector?.type === graphql.ConnectorType.Afas} />
						<EmployesTile selected={workspaceConnector?.type === graphql.ConnectorType.Employes} />
						<HoorayTile selected={workspaceConnector?.type === graphql.ConnectorType.Hooray} />
						<LoketTile selected={workspaceConnector?.type === graphql.ConnectorType.Loket} />
						<NmbrsTile selected={workspaceConnector?.type === graphql.ConnectorType.Nmbrs} />
						<SDWorxTile selected={workspaceConnector?.type === graphql.ConnectorType.Cobra} />
						<YouServeTile selected={workspaceConnector?.type === graphql.ConnectorType.Youserve} />
						<YouForceTile selected={workspaceConnector?.type === graphql.ConnectorType.Youforce} />
						<HR2DayTile selected={workspaceConnector?.type === graphql.ConnectorType.Hr2Day} />
						<VismaHRMTile selected={workspaceConnector?.type === graphql.ConnectorType.Vismahrm} />

						<ScimTile selected={workspaceConnector?.type === graphql.ConnectorType.Scim} />
						<StyrTile selected={hasStyrConnector} />
					</>
				) : (
					<Skeleton.Repeat count={3}>
						<Card.Skeleton height={200} />
					</Skeleton.Repeat>
				)}
			</Grid>

			{goodHabitzFeature.available && (
				<>
					<Separator mt={`${tokens.gap.section}px`} />
					<Heading3 mt={`${tokens.gap.section}px`}>
						{t('settings.connectors.marketplace.learning')}
					</Heading3>
					<Grid pt={3} pb={4}>
						{marketplaceData ? (
							<GoodHabitzTile selected={hasGoodHabitzConnector} />
						) : (
							<Skeleton.Repeat count={1}>
								<Card.Skeleton height={200} />
							</Skeleton.Repeat>
						)}
					</Grid>
				</>
			)}

			<Separator mt={`${tokens.gap.section}px`} />
			<Heading3 mt={`${tokens.gap.section}px`}>
				{t('settings.connectors.marketplace.messaging')}
			</Heading3>
			<Grid pt={3} pb={4}>
				{slackData ? (
					<SlackTile selected={slackData?.viewer.workspace.slackConfig?.isConfigured ?? false} />
				) : (
					<Skeleton.Repeat count={3}>
						<Card.Skeleton height={200} />
					</Skeleton.Repeat>
				)}
				{teamsData ? (
					<MicrosoftTile
						selected={teamsData?.viewer.workspace.microsoftConfig?.isConfigured ?? false}
					/>
				) : (
					<Skeleton.Repeat count={3}>
						<Card.Skeleton height={200} />
					</Skeleton.Repeat>
				)}
				{canManageMcp &&
					(mcpData ? (
						<McpRemoteTile selected={mcpData.mcpRemoteSettings.enabled} />
					) : (
						<Card.Skeleton height={200} />
					))}
			</Grid>

			{showDirectApi && (
				<>
					<Separator mt={`${tokens.gap.section}px`} />
					<Heading3 mt={`${tokens.gap.section}px`}>
						{t('settings.connectors.marketplace.directApi')}
					</Heading3>
					<Grid pt={3} pb={4}>
						{apiUsersData ? (
							<GraphqlApiTile selected={apiUsersData.apiUsers.length > 0} />
						) : (
							<Card.Skeleton height={200} />
						)}
					</Grid>
				</>
			)}
		</Container>
	);
}
